import { useTranslation } from 'react-i18next';
import { Link as LinkIcon } from 'lucide-react';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import PlatformIcon from '@components/profile/connections/platform-icon';
import ConnectionStatusBadge from '@components/profile/connections/connection-status-badge';
import { getPlatformSpec } from '@utils/types/connections/platforms.ts';
import { isPlatformAllowed } from '@utils/connections/policy.ts';
import type { Connection } from '@utils/types/connections/connection.ts';
import { formatDateYearMonthDay } from '@utils/date';

interface Props {
  connections: Connection[];
  isLoading: boolean;
  value: string | null;
  onChange: (connectionId: string) => void;
  onOpenConnectWizard?: () => void;
  canManageConnections: boolean;
}

/** Users pick from connections the organization already set up. */
export default function StepChooseConnection({
  connections,
  isLoading,
  value,
  onChange,
  onOpenConnectWizard,
  canManageConnections
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  // A platform the operator withdrew cannot publish, so it is not offered.
  const usable = connections.filter(
    c => c.status !== 'failed' && isPlatformAllowed(c)
  );

  if (!isLoading && usable.length === 0) {
    return (
      <div
        className="flex flex-col items-center gap-3 rounded-xl border p-6 text-center"
        style={{
          borderColor: getColour('hairline'),
          background: getColour('secondLayer')
        }}
      >
        <LinkIcon
          className="h-8 w-8"
          aria-hidden="true"
          style={{ color: getColour('inactiveText') }}
        />
        <p className="text-sm font-semibold">
          {t('publish-no-connections-title')}
        </p>
        <p
          className="max-w-sm text-xs"
          style={{ color: getColour('inactiveText') }}
        >
          {canManageConnections
            ? t('publish-no-connections-admin')
            : t('publish-no-connections-user')}
        </p>
        {canManageConnections && onOpenConnectWizard && (
          <button
            type="button"
            onClick={onOpenConnectWizard}
            className="text-sm font-medium underline"
            style={{ color: getColour('activeText') }}
          >
            {t('publish-open-connect-wizard')}
          </button>
        )}
      </div>
    );
  }

  return (
    <fieldset
      className="flex flex-col gap-3"
      aria-labelledby="publish-connection-legend"
    >
      <legend id="publish-connection-legend" className="sr-only">
        {t('publish-step-choose-connection')}
      </legend>
      <p className="text-sm" style={{ color: getColour('inactiveText') }}>
        {t('publish-choose-connection-help')}
      </p>
      <ul className="flex flex-col gap-2" aria-busy={isLoading}>
        {usable.map(connection => {
          const spec = getPlatformSpec(connection.platform);
          const selected = value === connection.id;
          const needsReconnect = connection.status === 'needs_reconnect';
          const inputId = `publish-connection-${connection.id}`;
          return (
            <li key={connection.id}>
              <label
                htmlFor={inputId}
                className="relative flex cursor-pointer items-center gap-3 rounded-xl border p-3 outline-none focus-within:ring-2"
                style={{
                  borderColor: selected
                    ? getColour('activeText')
                    : getColour('hairline'),
                  background: getColour('secondLayer'),
                  opacity: needsReconnect ? 0.75 : 1
                }}
              >
                <input
                  id={inputId}
                  type="radio"
                  name="publish-connection"
                  className="sr-only focus:outline-none"
                  value={connection.id}
                  checked={selected}
                  disabled={needsReconnect}
                  onChange={() => onChange(connection.id)}
                />
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                  style={{ background: getColour('thirdLayer') }}
                >
                  <PlatformIcon
                    platform={connection.platform}
                    className="h-5 w-5"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold truncate">
                      {spec?.displayName ?? connection.platform}
                    </span>
                    <ConnectionStatusBadge status={connection.status} />
                  </div>
                  <span
                    className="text-xs truncate block"
                    style={{ color: getColour('inactiveText') }}
                    title={connection.account_name}
                  >
                    {connection.account_name}
                  </span>
                  {(connection.connected_by || connection.connected_at) && (
                    <span
                      className="text-xs truncate block"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {connection.connected_by && (
                        <>
                          {t('publish-connection-connected-by', {
                            name:
                              connection.connected_by.email ||
                              connection.connected_by.name
                          })}
                        </>
                      )}
                      {connection.connected_by &&
                        connection.connected_at &&
                        ' · '}
                      {connection.connected_at && (
                        <>
                          {t('publish-connection-connected-at', {
                            date: formatDateYearMonthDay(
                              connection.connected_at
                            )
                          })}
                        </>
                      )}
                    </span>
                  )}
                </div>
              </label>
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}
